import React from "react";
import { Link } from "react-router-dom";
import { AiOutlineFileSearch, AiOutlineSetting, AiOutlineSafety, AiOutlineCheck, AiOutlineKey, AiOutlineBarChart, AiOutlineHistory } from "react-icons/ai";
import useUser from "../hooks/useUser";
import UnauthorizedMessage from "../components/UnauthorizedMessage";
import { ENV } from "../config/env";
import { colors, spacing, typography, radius, shadow, transitions } from "../theme/tokens";

/**
 * LandingPage — redistribuida por categorías para dar jerarquía a las tareas (P2 #70).
 *
 * Secciones:
 *  - "Mis tareas diarias" — lo que el usuario usa a diario (doctree, control de calidad)
 *  - "Administración" — solo ADMIN; config del sistema (series, acls, api keys, retención, reindex)
 *  - "Herramientas personales" — mis api keys, telegram (si aplica)
 *  - "Estadísticas" — visible para cualquier rol
 *
 * Los cards se ordenan según frecuencia de uso esperada por rol.
 */
const LandingPage = () => {
  const { hasRole, roles } = useUser();

  if (roles && roles.length === 0) {
    return <UnauthorizedMessage />;
  }

  const dailyCards = [];
  const adminCards = [];
  const personalCards = [];
  const statsCards = [];

  if (hasRole('VIEWER')) {
    dailyCards.push({
      icon: <AiOutlineFileSearch />,
      title: 'Árbol Documental',
      description: 'Navegá y buscá documentos organizados por unidad académica, departamento y tipo.',
      linkLabel: 'Abrir árbol',
      to: '/doctree',
    });
  }
  // El Firmador entra acá igual que el Revisor y el Aprobador: firmar es un estado más del workflow
  // y vive como pestaña de Control de Calidad (#59). El menú hamburguesa y la ruta ya lo contemplaban;
  // esta pantalla no, y es la primera que ve el usuario al entrar. Un firmante mono-rol aterrizaba en
  // una pantalla con una sola tarjeta —Estadísticas— y sin ninguna vía visible hacia donde firma
  // (GitLab #33/#34).
  if (hasRole('REVIEWER') || hasRole('APPROVER') || hasRole('SIGNER')) {
    dailyCards.push({
      icon: <AiOutlineCheck />,
      title: 'Control de calidad',
      description: 'Revisá, aprobá y firmá documentos pendientes.',
      linkLabel: 'Abrir control de calidad',
      to: '/quality-control',
    });
  }

  if (hasRole('ADMIN')) {
    adminCards.push(
      {
        icon: <AiOutlineSetting />,
        title: 'Series documentales',
        description: 'Gestioná series, flujos y especificaciones de metadatos.',
        linkLabel: 'Gestionar series',
        to: '/admin/specs',
      },
      {
        icon: <AiOutlineSafety />,
        title: 'Gestión de ACLs',
        description: 'Administrá los permisos de acceso a las series documentales.',
        linkLabel: 'Gestionar ACLs',
        to: '/admin/acls',
      },
      {
        icon: <AiOutlineKey />,
        title: 'API Keys del sistema',
        description: 'Claves de todos los usuarios y servicios (importadores, bots, integraciones).',
        linkLabel: 'Gestionar API keys',
        to: '/admin/api-keys',
      },
      {
        icon: <AiOutlineHistory />,
        title: 'Políticas de retención',
        description: 'Retención y disposición final de series documentales.',
        linkLabel: 'Gestionar políticas',
        to: '/admin/retention-policies',
      },
    );
  }

  if (hasRole('IMPORTER')) {
    personalCards.push({
      icon: <AiOutlineKey />,
      title: 'Mis API keys',
      description: 'Claves para herramientas externas y boveda-cli.',
      linkLabel: 'Mis claves',
      to: '/api-keys',
    });
  }

  statsCards.push({
    icon: <AiOutlineBarChart />,
    title: 'Estadísticas de series',
    description: 'Conteos de documentos por estado en cada serie.',
    linkLabel: 'Ver estadísticas',
    to: '/admin/stats',
  });

  return (
    <div style={styles.container}>
      <div style={styles.content}>
        <header style={styles.hero}>
          <h1 style={styles.title}>{ENV.APP_NAME}</h1>
          <p style={styles.subtitle}>
            {ENV.LANDING_SUBTITLE || "Sistema de gestión documental"}
          </p>
        </header>

        {dailyCards.length > 0 && (
          <Section title="Mis tareas diarias" emphasis>
            <CardGrid cards={dailyCards} emphasis />
          </Section>
        )}

        {adminCards.length > 0 && (
          <Section title="Administración">
            <CardGrid cards={adminCards} />
          </Section>
        )}

        {personalCards.length > 0 && (
          <Section title="Herramientas personales">
            <CardGrid cards={personalCards} />
          </Section>
        )}

        <Section title="Estadísticas">
          <CardGrid cards={statsCards} />
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children, emphasis }) => (
  <section style={styles.section}>
    <h2 style={{ ...styles.sectionTitle, ...(emphasis ? styles.sectionTitleEmphasis : {}) }}>{title}</h2>
    {children}
  </section>
);

const CardGrid = ({ cards, emphasis }) => (
  <div style={emphasis ? styles.gridEmphasis : styles.grid}>
    {cards.map((c) => (
      <FeatureCard key={c.to} {...c} emphasis={emphasis} />
    ))}
  </div>
);

const FeatureCard = ({ icon, title, description, linkLabel, to, emphasis }) => (
  <article style={{ ...styles.card, ...(emphasis ? styles.cardEmphasis : {}) }}>
    <div style={{ ...styles.icon, ...(emphasis ? styles.iconEmphasis : {}) }}>{icon}</div>
    <h3 style={styles.cardTitle}>{title}</h3>
    <p style={styles.cardDescription}>{description}</p>
    <Link to={to} style={styles.cardButton}>
      {linkLabel}
    </Link>
  </article>
);

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    padding: `${spacing[6]}px ${spacing[4]}px`,
    backgroundColor: colors.bg,
    minHeight: '100%',
  },
  content: {
    maxWidth: 1200,
    width: '100%',
  },
  hero: {
    textAlign: 'center',
    marginBottom: spacing[7],
  },
  title: {
    fontSize: typography.fontSize['4xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.primary,
    margin: `0 0 ${spacing[3]}px 0`,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: typography.fontSize.xl,
    color: colors.textMuted,
    margin: 0,
  },
  section: {
    marginBottom: spacing[7],
  },
  sectionTitle: {
    fontSize: typography.fontSize.xl,
    color: colors.text,
    margin: `0 0 ${spacing[4]}px 0`,
    fontWeight: typography.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    paddingBottom: spacing[2],
  },
  sectionTitleEmphasis: {
    borderBottom: `2px solid ${colors.primary}`,
    color: colors.primary,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: spacing[4],
  },
  gridEmphasis: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: spacing[5],
  },
  card: {
    backgroundColor: colors.surface,
    padding: spacing[5],
    borderRadius: radius.md,
    border: `1px solid ${colors.border}`,
    boxShadow: shadow.sm,
    transition: `transform ${transitions.base}, box-shadow ${transitions.base}, border-color ${transitions.base}`,
  },
  cardEmphasis: {
    padding: spacing[6],
    boxShadow: shadow.md,
    borderColor: colors.primaryLight,
  },
  icon: {
    fontSize: typography.fontSize['3xl'],
    color: colors.primary,
    marginBottom: spacing[3],
    display: 'inline-flex',
  },
  iconEmphasis: {
    fontSize: typography.fontSize['4xl'],
  },
  cardTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    margin: `0 0 ${spacing[2]}px 0`,
  },
  cardDescription: {
    fontSize: typography.fontSize.base,
    color: colors.textMuted,
    margin: `0 0 ${spacing[4]}px 0`,
    lineHeight: typography.lineHeight.normal,
  },
  cardButton: {
    display: 'inline-block',
    backgroundColor: colors.primary,
    color: colors.textInverse,
    padding: `${spacing[2]}px ${spacing[4]}px`,
    borderRadius: radius.sm,
    textDecoration: 'none',
    fontSize: typography.fontSize.base,
    fontWeight: typography.fontWeight.semibold,
    transition: `background-color ${transitions.base}`,
  },
};

export default LandingPage;
